import { buttonClasses, type ButtonProps } from '@mui/material/Button';
import { alpha, type Components, lighten, type Theme } from '@mui/material/styles';

import getStyleQuery from '../../../utils/tools/getStyleQuery';
import { getAccessibleTextColor } from '../../getAccessibleTextColor';

const COLORS = ['primary', 'secondary', 'info', 'success', 'warning', 'error'] as const;

// NEW VARIANT
declare module '@mui/material/Button' {
    interface ButtonPropsVariantOverrides {
        soft: true;
    }
}

// ----------------------------------------------------------------------

export function button(theme: Theme): Components<Theme> {
    const lightMode = theme.palette.mode === 'light';

    const rootStyles = (ownerState: ButtonProps) => {
        const inheritColor = ownerState.color === 'inherit';

        const containedVariant = ownerState.variant === 'contained';

        const outlinedVariant = ownerState.variant === 'outlined';

        const textVariant = ownerState.variant === 'text';

        const softVariant = ownerState.variant === 'soft';

        const smallSize = ownerState.size === 'small';

        const mediumSize = ownerState.size === 'medium';

        const largeSize = ownerState.size === 'large';

        const defaultStyle = {
            ...(inheritColor && {
                // CONTAINED
                ...(containedVariant && {
                    color: lightMode ? theme.palette.common.white : theme.palette.grey[800],
                    backgroundColor: lightMode ? theme.palette.grey[800] : theme.palette.common.white,
                    '&:hover': {
                        backgroundColor: lightMode ? theme.palette.grey[700] : theme.palette.grey[400],
                    },
                }),
                // OUTLINED
                ...(outlinedVariant && {
                    borderColor: alpha(theme.palette.grey[500], 0.32),
                    '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                    },
                }),
                // TEXT
                ...(textVariant && {
                    '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                    },
                }),
                // SOFT
                ...(softVariant && {
                    color: theme.palette.text.primary,
                    backgroundColor: lighten(theme.palette.grey[500], 0.6),
                    '&:hover': {
                        backgroundColor: lighten(theme.palette.grey[500], 0.4),
                    },
                }),
            }),
            ...(outlinedVariant && {
                '&:hover': {
                    borderColor: 'currentColor',
                    boxShadow: '0 0 0 0.5px currentColor',
                },
            }),
            ...(ownerState.variant === 'soft' && {
                [`& .${buttonClasses.loadingPositionStart}`]: {
                    left: 10,
                },
                [`& .${buttonClasses.loadingPositionEnd}`]: {
                    right: 14,
                },
                ...(ownerState.size === 'small' && {
                    [`& .${buttonClasses.loadingPositionStart}`]: {
                        left: 10,
                    },
                    [`& .${buttonClasses.loadingPositionEnd}`]: {
                        right: 10,
                    },
                }),
            }),
        };

        const colorStyle = COLORS.map((color) => {
            const baseBgColor = lighten(theme.palette[color].main, 0.85);
            const baseTextColor = theme.palette[color][lightMode ? 'dark' : 'light'];

            // Adjust text color if contrast is too low
            const accessibleTextColor = getAccessibleTextColor(theme, baseBgColor, baseTextColor);

            return {
                ...(ownerState.color === color && {
                    // CONTAINED
                    ...(containedVariant && {
                        '&:hover': {
                            boxShadow: theme.customShadows[color],
                        },
                    }),
                    // SOFT
                    ...(softVariant && {
                        color: accessibleTextColor,
                        backgroundColor: baseBgColor,
                        '&:hover': {
                            backgroundColor: lighten(theme.palette[color].main, 0.75),
                            boxShadow: theme.customShadows[color],
                        },
                    }),
                }),
            };
        });

        const disabledState = {
            [`&.${buttonClasses.disabled}`]: {
                // SOFT
                ...(softVariant && {
                    backgroundColor: theme.palette.action.disabledBackground,
                }),
            },
        };

        const size = {
            ...(smallSize && {
                height: 30,
                fontSize: theme?.rawTypography?.body2?.fontSize,
                paddingLeft: 8,
                paddingRight: 8,
                ...(textVariant && {
                    paddingLeft: 4,
                    paddingRight: 4,
                }),
            }),
            ...(mediumSize && {
                paddingLeft: 12,
                paddingRight: 12,
                ...(textVariant && {
                    paddingLeft: 8,
                    paddingRight: 8,
                }),
            }),
            ...(largeSize && {
                height: 48,
                fontSize: theme?.rawTypography?.body1?.fontSize,
                paddingLeft: 16,
                paddingRight: 16,
                ...(textVariant && {
                    paddingLeft: 10,
                    paddingRight: 10,
                }),
            }),
        };

        return [defaultStyle, ...colorStyle, disabledState, size];
    };

    return {
        MuiButton: {
            styleOverrides: {
                root: ({ ownerState }: { ownerState: ButtonProps }) => ({
                    [getStyleQuery()]: rootStyles(ownerState),
                }),
            },
        },
    };
}
